import { getDebtorProfile } from "Application/User/DebtorService";
import { UserProfile } from "Domain/User/Entity/UserProfile";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import DisplayDebtor from "./Parts/DisplayDebtor";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import PaymentPlanForm from "./Parts/PaymentPlanForm";
import DisplayPayIns from "./Parts/DisplayPayIns";
import { FormFieldsConfig } from "pages/components/Form/FormEntities";
import useForm from "pages/components/Form/useForm";
import FormFields from "pages/components/FormFields";
import DisplayPayOuts from "./Parts/DisplayPayOuts";
import EntityProvider from "pages/components/Behavior/EntityProvider";
import Button from "pages/components/FormFields/Button";
import { useNavigate } from "react-router-dom";
import {
  getUrl,
  DEBTORS_LIST,
  PAYMENT_PLAN_DETAIL,
} from "Infrastructure/config/routesConfig";
import { CreatePaymentPlanRequest } from "Domain/PaymentPlan/Dto/CreatePaymentPlanRequest";
import { createPaymentPlan } from "Application/PaymentPlan/PaymentPlanService";
import { formatPrice, formatPercent } from "pages/Utils/Formats";
import { PaymentPlanConfig } from "Domain/PaymentPlan/Helper/PaymentsCalculator";

export default function PaymentPlanCreate() {
  const ranOnce = useRef<boolean>(false);
  const navigate = useNavigate();

  const [disableNumberOfSequences, setDisableNumberOfSequences] =
    useState(false);

  const queryString = window.location.search;
  const { t } = useTranslation();
  const urlParams = new URLSearchParams(queryString);

  let debtorId = urlParams.get("debtor");
  debtorId = debtorId ? debtorId : "-";

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  var PaymentPlanFormConfig = getPaymentPlanFormConfig();

  const {
    errors,
    values,
    options,
    updateValues,
    handleInputChange,
    isValidForm,
    formatErrorObject,
    Form,
  } = useForm(PaymentPlanFormConfig, true);

  const paymentPlanRequest = getCreatePaymentPlanRequest(values, debtorId!);
  const paymentPlanConfig = new PaymentPlanConfig(paymentPlanRequest);

  function handleSubmit() {
    if (isValidForm()) {
      createPaymentPlan(paymentPlanRequest).then((res: any) => {
        if (!("error" in res)) {
          navigate(getUrl(PAYMENT_PLAN_DETAIL, { id: res.publicId }));
        }
      });
    }
  }

  useEffect(() => {
    if (
      ranOnce.current &&
      (typeof values.financedAmount === "number" ||
        values.financedAmount === "")
    ) {
      if (values.financedAmount !== "") {
        handleInputChange({
          target: {
            name: "initialAmount",
            value: Math.ceil(
              values.financedAmount *
                paymentPlanConfig.getInitialAmountRatios().min
            ),
          },
        });
      } else {
        updateValues({
          initialAmount: "",
        });
      }
    }
    ranOnce.current = true;
  }, [values.financedAmount, values.secured, values.numberOfSessions]);

  useEffect(() => {
    if (
      ranOnce.current &&
      (typeof values.financedAmount === "number" ||
        values.financedAmount === "")
    ) {
      if (values.financedAmount !== "") {
        handleInputChange({
          target: {
            name: "numberOfSequences",
            value: paymentPlanConfig.getDefaultNumberOfSequences(),
          },
        });
      }
    }
    ranOnce.current = true;
  }, [values.financedAmount]);

  useEffect(() => {
    if (values.numberOfSessions === 2) {
      handleInputChange({
        target: {
          name: "numberOfSequences",
          value: 12,
        },
      });
      setDisableNumberOfSequences(true);
    } else {
      setDisableNumberOfSequences(false);
    }
  }, [values.numberOfSessions]);

  useEffect(() => {
    handleInputChange({
      target: {
        name: "numberOfSessions",
        value: 1,
      },
    });
  }, [values.secured]);

  if (debtorId === "-") {
    return <DebtorNotFound />;
  }

  return (
    <EntityProvider<UserProfile>
      callback={getDebtorProfile}
      notFoundComponent={<DebtorNotFound />}
      params={[debtorId]}
    >
      {(debtor, setUserData) => (
        <Box>
          <Form onSubmit={handleSubmit}>
            <Box display={"grid"} gap={2} width={"100%"}>
              <Box
                display={"grid"}
                gridTemplateColumns={isMobile ? "1fr" : "1.2fr 3fr 1fr"}
                gap={2}
                width={"100%"}
              >
                {debtor && (
                  <>
                    <DisplayDebtor debtor={debtor} />
                    <PaymentPlanForm
                      handleInputChange={handleInputChange}
                      values={values}
                      formatErrorObject={formatErrorObject}
                      options={options}
                      disableNumberOfSequences={disableNumberOfSequences}
                      errors={errors}
                    />
                    <DisplayPayIns paymentPlanConfig={paymentPlanConfig} />
                  </>
                )}
              </Box>
              <DisplayPayOuts paymentPlanConfig={paymentPlanConfig} />
            </Box>

            <FormFields.Button
              type="submit"
              label={t(`FORM.SUBMIT.create`)}
              fullWidth
              sx={{ mt: 2 }}
            />
          </Form>
        </Box>
      )}
    </EntityProvider>
  );
}

function getPaymentPlanFormConfig() {
  return {
    quote: {
      default: "",
    },
    financedAmount: {
      default: "",
      constraints: [
        "required",
        {
          message: (values: any) => {
            const financedAmountRange = new PaymentPlanConfig(
              getCreatePaymentPlanRequest(values, "-")
            ).getFinancedAmountRange();
            return {
              name: "financed_amount_range",
              params: {
                min: formatPrice(financedAmountRange.min),
                max: formatPrice(financedAmountRange.max),
              },
            };
          },
          validation: (value: string, values: any) => {
            const financedAmountRange = new PaymentPlanConfig(
              getCreatePaymentPlanRequest(values, "-")
            ).getFinancedAmountRange();
            return (
              value === "" ||
              (parseInt(value) >= financedAmountRange.min &&
                parseInt(value) <= financedAmountRange.max)
            );
          },
        },
      ],
    },
    initialAmount: {
      default: "",
      constraints: [
        "required",
        {
          message: (values: any) => {
            const paymentPlanConfig =  new PaymentPlanConfig(
              getCreatePaymentPlanRequest(values, "-")
            );
            const initialAmountRatios = paymentPlanConfig.getInitialAmountRatios();
            const financedAmount = paymentPlanConfig.paymentPlanRequest.financedAmount;
            return {
              name: "initial_amount_ratio",
              params: {
                min: formatPercent(initialAmountRatios.min * 100),
                max: formatPercent(initialAmountRatios.max * 100),
                valueMin: formatPrice(initialAmountRatios.min * financedAmount),
                valueMax: formatPrice(initialAmountRatios.max * financedAmount),
              },
            };
          },
          validation: (value: string, values: any) => {
            const initialAmountRatios = new PaymentPlanConfig(
              getCreatePaymentPlanRequest(values, "-")
            ).getInitialAmountRatios();
            return (
              value === "" ||
              (parseInt(value) >=
                values.financedAmount * initialAmountRatios.min &&
                parseInt(value) <=
                  values.financedAmount * initialAmountRatios.max)
            );
          },
        },
      ],
    },
    numberOfSequences: {
      default: 12,
      constraints: ["required"],
      options: [2, 4, 6, 8, 10, 12].map((key) => ({
        label: `${key}`,
        value: key,
      })),
    },
    numberOfSessions: {
      default: 1,
      constraints: ["required"],
      options: [1, 2].map((key) => ({ label: `${key}`, value: key })),
    },
    secured: {
      default: false,
      constraints: ["required"],
    },
  } as FormFieldsConfig;
}

function DebtorNotFound() {
  const { t } = useTranslation();

  const navigate = useNavigate();
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Typography variant="h5" marginBottom={3}>
        {t("CONTENT.select_debtor")}
      </Typography>
      <Button
        onClick={() => {
          navigate(getUrl(DEBTORS_LIST));
        }}
        label={"Patients List"}
      />
    </Box>
  );
}

function getCreatePaymentPlanRequest(values: any, debtorId: string) {
  return new CreatePaymentPlanRequest(
    debtorId,
    values.quote,
    values.financedAmount,
    values.initialAmount,
    values.numberOfSequences,
    values.numberOfSessions,
    values.secured
  );
}
